import ReactMarkdown from "react-markdown";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchData,
  selectIsLoading,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  setChatbotId,
  saveChatHistory,
  setOpenAiTokens,
  selectOpenAiTokens,
} from "../../../../mainApp/features/DataSlice/dataSlice";
import {
  ChatbotContainer,
  ChatbotMain as OriginalChatbotMain,
  ChatbotForm,
  ChatbotInput,
  ChatbotButton,
  ChatbotError,
  ChatbotMessage,
  AppTitle,
  LoadingElement,
} from "../styled";
import LoadingPage from "../../TechnicalTabs/LoadingPage";
import ErrorPage from "../../../../mainApp/features/TechnicalTabs/ErrorPage";
import { auth, db } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { handleUnauthorizedAccess } from "../../Utility/handleUnauthorizedAccess";
import { fetchCasualResponse } from "./apiService";
import styled from "styled-components";
import { CASUAL_RESPONSE_COST } from "../../Utility/tokenCosts";

const CasualChatbotMain = styled(OriginalChatbotMain)`
  @media (max-width: 1460px) {
    height: 100%;
    width: 100%;
    padding-bottom: 80px;
  }
`;

// Styl dla historii czatu
const ChatHistory = styled.div`
  max-height: 600px;
  overflow-y: auto;
  margin-top: 20px;
  padding: 10px;
  background: #282828;
  border-radius: 8px;
`;

// Styl dla wiadomości w czacie
const ChatMessage = styled.div`
  margin: 10px 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${(props) => (props.isUser ? "#1db954" : props.isContinuation ? "#4a4a4a" : "#3c3c3c")};
  color: ${(props) => (props.isUser ? "#000000" : "#ffffff")};
  align-self: ${(props) => (props.isUser ? "flex-end" : "flex-start")};
  max-width: 80%;
  position: relative;

  h1, h2, h3, h4, h5, h6 {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  p {
    margin: 0;
    line-height: 1.6;
  }

  ul, ol {
    margin: 10px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 10px;
  }

  blockquote {
    border-left: 3px solid #ccc;
    padding-left: 10px;
    margin-left: 0;
    color: #ccc;
  }

  code {
    background-color: #282c34;
    padding: 2px 4px;
    border-radius: 4px;
    font-family: monospace;
  }

  pre {
    background-color: #282c34;
    padding: 10px;
    border-radius: 4px;
    overflow-x: auto;
  }

  ${(props) =>
    props.isContinuation &&
    `
    &:before {
      content: "Kontynuacja";
      font-size: 10px;
      color: #999;
      position: absolute;
      top: -12px;
      left: 12px;
    }
  `}
`;

// Styl dla komunikatu o limicie
const LimitMessage = styled.div`
  color: #ff4d4d;
  font-size: 14px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

// Styl dla przycisku "TAK"
const ContinueButton = styled.button`
  padding: 5px 10px;
  background-color: #1db954;
  border: none;
  border-radius: 4px;
  color: #000000;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #1ed760;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

const ChatbotCasual = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);
  const currentTokens = useSelector(selectOpenAiTokens);
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([
    { text: "Wpisz swoje pytanie", isUser: false },
  ]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sessionId] = useState(Date.now().toString()); // Unikalny ID sesji
  const [openAiTokens, setOpenAiTokensState] = useState(0);

  const validationToastId = 'casual-bot-validation-toast';

  // Funkcja opóźnienia
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(setChatbotId("casual")); // Ustawienie chatbotId dla spójności
    dispatch(fetchData());
    return () => {
    };
  }, [dispatch, sessionId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          const ref = doc(db, "Users", currentUser.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            console.warn("Dokument użytkownika nie istnieje.");
          }
          await delay(600); // Dodatkowe opóźnienie jako zabezpieczenie
        } catch (error) {
          console.error("Błąd pobierania danych użytkownika:", error);
          await delay(600); // Opóźnienie w przypadku błędu
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate("/unauthorized");
    }
  }, [restrictedDataError, navigate]);

  useEffect(() => {
    if (currentTokens !== openAiTokens) {
      setOpenAiTokensState(currentTokens);
    }
  }, [currentTokens]);

  const handleChange = (e) => setMessage(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError("Proszę wpisać wiadomość.");
      return;
    }

    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < CASUAL_RESPONSE_COST) {
      toast.error("Masz za mało tokenów, aby wysłać wiadomość.", toastOptions);
      return;
    }

    setError(null);
    setIsFetching(true);
    const userMessage = message.trim();
    const updatedHistory = [...chatHistory, { text: userMessage, isUser: true }];
    setChatHistory(updatedHistory);
    setMessage("");

    try {
      const response = await fetchCasualResponse(userMessage, chatHistory);

      if (response && response.remainingTokens !== undefined) {
        dispatch(setOpenAiTokens(response.remainingTokens));
      }

      const finalHistory = [
        ...updatedHistory,
        {
          text: response.content,
          isUser: false,
          isTruncated: response.isTruncated,
        },
      ];
      setChatHistory(finalHistory);

      // Zapisz historię w Firestore jako jeden dokument dla sesji
      if (user) {
        const filteredHistory = finalHistory.filter(
          (msg, index) => !(index === 0 && !msg.isUser)
        );
        if (filteredHistory.length > 0) {
          const firstUserMessage = filteredHistory.find((msg) => msg.isUser)?.text || "Rozmowa";
          const historyData = {
            chatbotId: "casual",
            title: firstUserMessage.slice(0, 50),
            content: JSON.stringify(filteredHistory),
            timestamp: new Date().toISOString(),
          };
          
          await setDoc(doc(db, `Users/${user.uid}/chatHistory`, sessionId), historyData);
          dispatch({ type: "FETCH_CHAT_HISTORY" }); // Odśwież historię
        }
      } else {
        console.warn("No user logged in, skipping history save.");
      }
    } catch (err) {
      setError(err.message);
      setChatHistory((prev) => [
        ...prev,
        { text: "Wystąpił błąd podczas generowania odpowiedzi.", isUser: false },
      ]);
    } finally {
      setIsFetching(false);
    }
  };

  const handleContinue = async () => {
    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < CASUAL_RESPONSE_COST) {
      toast.error("Masz za mało tokenów, aby kontynuować rozmowę.", toastOptions);
      return;
    }

    setError(null);
    setIsFetching(true);

    try {
      const response = await fetchCasualResponse("", chatHistory, true);

      if (response && response.remainingTokens !== undefined) {
        dispatch(setOpenAiTokens(response.remainingTokens));
      }

      const finalHistory = [
        ...chatHistory,
        {
          text: response.content,
          isUser: false,
          isTruncated: response.isTruncated,
          isContinuation: true, // Oznaczamy jako kontynuację
        },
      ];
      setChatHistory(finalHistory);

      // Zapisz zaktualizowaną historię w Firestore
      if (user) {
        const filteredHistory = finalHistory.filter(
          (msg, index) => !(index === 0 && !msg.isUser)
        );
        if (filteredHistory.length > 0) {
          const firstUserMessage = filteredHistory.find((msg) => msg.isUser)?.text || "Rozmowa";
          const historyData = {
            chatbotId: "casual",
            title: firstUserMessage.slice(0, 50),
            content: JSON.stringify(filteredHistory),
            timestamp: new Date().toISOString(),
          };
         
          await setDoc(doc(db, `Users/${user.uid}/chatHistory`, sessionId), historyData);
          dispatch({ type: "FETCH_CHAT_HISTORY" }); // Odśwież historię
        }
      }
    } catch (err) {
      setError(err.message);
      setChatHistory((prev) => [
        ...prev,
        { text: "Wystąpił błąd podczas generowania kontynuacji.", isUser: false },
      ]);
    } finally {
      setIsFetching(false);
    }
  };

  if (initialLoading || isLoading === "loading") {
    return <LoadingPage />;
  }

  let returned;

  switch (isLoading) {
    case "success":
      if (!user) {
        returned = <LoadingPage isHighZIndex={true} />;
      } else if (tilesDetails.length > 0 && user) {
        returned = (
          <ChatbotContainer>
            <CasualChatbotMain>
              <AppTitle>Luźna Rozmowa</AppTitle>
              <ChatbotForm onSubmit={handleSubmit}>
                <ChatbotInput
                  type="text"
                  value={message}
                  onChange={handleChange}
                  placeholder="Napisz coś..."
                  disabled={isFetching}
                />
                <ChatbotButton type="submit" disabled={isFetching} tokenCost={CASUAL_RESPONSE_COST}>
                  Wyślij
                </ChatbotButton>
              </ChatbotForm>
              <ChatHistory>
                {chatHistory.map((msg, index) => (
                  <div key={index}>
                    <ChatMessage
                      isUser={msg.isUser}
                      isContinuation={msg.isContinuation}
                    >
                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                    </ChatMessage>
                    {msg.isTruncated && !msg.isUser && (
                      <LimitMessage>
                        Wykorzystałeś limit jednej odpowiedzi, jeżeli chcesz kontynuacji, kliknij TAK
                        <ContinueButton
                          onClick={() => handleContinue()}
                          disabled={isFetching}
                        >
                          TAK
                        </ContinueButton>
                      </LimitMessage>
                    )}
                  </div>
                ))}
              </ChatHistory>
              {isFetching && <LoadingElement>Przetwarzanie...</LoadingElement>}
              {error && <ChatbotError>{error}</ChatbotError>}
            </CasualChatbotMain>
          </ChatbotContainer>
        );
      } else {
        returned = handleUnauthorizedAccess();
      }
      break;
    case "error":
      returned = <ErrorPage />;
      break;
    default:
      returned = <LoadingPage />;
  }

  return returned;
};

export default ChatbotCasual;